/**
 * Serviço para gerenciar temas da aplicação
 */
class ThemeService {
  constructor() {
    this.storageKey = 'app-theme'
    this.defaultTheme = this.getSystemTheme()
  }

  /**
   * Detecta o tema preferido do sistema
   */
  getSystemTheme() {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
    }
    return 'light'
  }

  /**
   * Obtém o tema atual
   */
  getCurrentTheme() {
    try {
      const stored = localStorage.getItem(this.storageKey)
      return stored || this.defaultTheme
    } catch (error) {
      console.warn('Erro ao ler tema do localStorage:', error)
      return this.defaultTheme
    }
  }

  /**
   * Define o tema
   */
  setTheme(theme) {
    try {
      localStorage.setItem(this.storageKey, theme)
      this.applyTheme(theme)
      return true
    } catch (error) {
      console.warn('Erro ao salvar tema no localStorage:', error)
      return false
    }
  }

  /**
   * Aplica o tema na aplicação
   */
  applyTheme(theme) {
    if (typeof window !== 'undefined') {
      // Aplicar no Vuetify se disponível
      if (window.Vue && window.Vue.prototype.$vuetify && window.Vue.prototype.$vuetify.theme) {
        window.Vue.prototype.$vuetify.theme.dark = theme === 'dark'
        // Forçar atualização do Vuetify
        window.Vue.prototype.$vuetify.theme.currentTheme = theme
      }
      
      // Aplicar classe no body para CSS customizado
      if (document.body) {
        document.body.classList.remove('theme-light', 'theme-dark')
        document.body.classList.add(`theme-${theme}`)
      }
      
      // Definir atributo data-theme para CSS
      if (document.documentElement) {
        document.documentElement.setAttribute('data-theme', theme)
        document.documentElement.className = document.documentElement.className.replace(/theme-\w+/g, '')
        document.documentElement.classList.add(`theme-${theme}`)
      }
      
      // Forçar re-renderização dos componentes Vue
      if (window.Vue && window.Vue.prototype.$forceUpdate) {
        // Aguardar um tick para garantir que as mudanças sejam aplicadas
        setTimeout(() => {
          window.Vue.prototype.$forceUpdate()
        }, 50)
      }
    }
  }

  /**
   * Alterna entre tema claro e escuro
   */
  toggleTheme() {
    const currentTheme = this.getCurrentTheme()
    const newTheme = currentTheme === 'light' ? 'dark' : 'light'
    this.setTheme(newTheme)
    return newTheme
  }

  /**
   * Inicializa o tema na aplicação
   */
  initializeTheme() {
    const theme = this.getCurrentTheme()
    this.applyTheme(theme)
    return theme
  }

  /**
   * Reseta o tema para o padrão do sistema
   */
  resetToSystemTheme() {
    const systemTheme = this.getSystemTheme()
    return this.setTheme(systemTheme)
  }

  /**
   * Verifica se o tema atual é escuro
   */
  isDark() {
    return this.getCurrentTheme() === 'dark'
  }

  /**
   * Verifica se o tema atual é claro
   */
  isLight() {
    return this.getCurrentTheme() === 'light'
  }
}

// Criar instância singleton
const themeService = new ThemeService()

export default themeService
